import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import UpdatedRelationshipsSection from '@/components/shared/UpdatedRelationshipsSection';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EditableRelationshipsSectionProps {
  formData: any;
  updateLocalData: (field: string, value: any) => void;
  syncWithAuthContext: (updatedFields: Record<string, any>) => Promise<void>;
  userId: string;
}

const EditableRelationshipsSection: React.FC<EditableRelationshipsSectionProps> = ({
  formData,
  updateLocalData,
  syncWithAuthContext,
  userId
}) => {
  const [isSaving, setIsSaving] = useState<string | null>(null);

  const relationshipsData = {
    relationshipType: formData.relationship_type,
    travelAvailability: formData.travel_availability,
    maritalStatus: formData.marital_status,
    sigilo_presenca: formData.sigilo_presenca
  };

  const handleUpdateData = async (field: string, value: string) => {
    const fieldMapping: Record<string, string> = {
      'relationshipType': 'relationship_type',
      'travelAvailability': 'travel_availability',
      'maritalStatus': 'marital_status',
      'sigilo_presenca': 'sigilo_presenca'
    };

    const backendField = fieldMapping[field] || field;
    
    // Update local state immediately
    updateLocalData(backendField, value);
    
    // Auto-save to database
    if (!userId) return;
    
    setIsSaving(backendField);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [backendField]: value })
        .eq('id', userId);

      if (error) throw error;

      await syncWithAuthContext({ [backendField]: value });
      toast.success('Salvo', { duration: 1500 });
    } catch (error) {
      console.error('Error updating relationships:', error);
      toast.error('Erro ao salvar');
    } finally {
      setIsSaving(null);
    }
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-luxury-red/20 rounded-full flex items-center justify-center">
              <span className="text-lg">💕</span>
            </div>
            <h3 className="text-white font-medium text-lg">Relacionamentos</h3>
          </div>
          {isSaving && (
            <span className="text-xs text-white/40">Salvando...</span>
          )}
        </div>

        <UpdatedRelationshipsSection
          data={relationshipsData}
          updateData={handleUpdateData}
          isOnboarding={false}
          isEditing={true}
        />
      </CardContent>
    </Card>
  );
};

export default EditableRelationshipsSection;
